/**
 * ConfirmDialog
 *
 * The plain "are you sure?" — a yes/no gate with no inputs. Built on
 * AlertDialog, which is what gives it the right focus trapping and
 * close-on-select semantics for a destructive prompt.
 *
 * When the action needs more than a yes:
 *
 *   - a mandatory reason, or
 *   - a second factor (your password, or an authorizing wristband)
 *
 * reach for `AuthorizeActionDialog` in `shared/components/AuthorizeAction`
 * instead. Do NOT stack the two — that dialog states the consequence and
 * demands a deliberate credential, so it already *is* the confirmation.
 *
 * This absorbed treasury's `ConfirmActionDialog` (2026-07-16), which was the
 * same component with `variant: 'archive' | 'delete'` in place of
 * `destructive: boolean`, plus a leading icon. Its one real advantage — a
 * ReactNode description — was kept.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/theme/components/ui/alert-dialog';
import { useTranslation } from '@/core';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Title displayed in the dialog header */
  title?: string;
  /** Description/body of the confirmation. ReactNode so callers can bold names. */
  description: React.ReactNode;
  /** Label for the confirm button */
  confirmLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Whether the action is destructive (red button) */
  destructive?: boolean;
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Whether the confirm action is in progress */
  isLoading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = true,
  onConfirm,
  isLoading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? t('common.areYouSure')}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelLabel ?? t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {isLoading ? t('common.processing') : (confirmLabel ?? t('common.confirm'))}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
